import React, { useEffect, useRef, useState } from 'react';
import { Link as ScrollLink } from 'react-scroll';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { scroller } from 'react-scroll';
import { t } from '../../Utills/translations';
import './CategoriesNavBar.css';

const sanitizeId = (str) =>
  String(str)
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9\-_]/g, '')
    .toLowerCase();

const CategoriesNavBar = ({
  categories,
  // themeColor,
  onCategoryClick,
  offset = -80,
  className = '',
  showAllButton = false,
  themeColor,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const navRef = useRef(null);
  const scrollTimeoutRef = useRef(null);

  const shouldShowAllButton = showAllButton;

  // Observa as seções para atualizar o link ativo
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -70% 0px',
      threshold: 0,
    };

    const observerCallback = (entries) => {
      if (isScrolling) return;

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id;
          const index = categories.findIndex(
            (cat) => sanitizeId(String(cat.id)) === sectionId,
          );
          if (index !== -1) {
            setActiveIndex(index);
          }
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions,
    );

    categories.forEach((categoria) => {
      const sectionId = sanitizeId(String(categoria.id));
      const section = document.getElementById(sectionId);
      if (section) {
        observer.observe(section);
      }
    });

    return () => observer.disconnect();
  }, [categories, isScrolling]);

  // Scroll da navbar para centralizar o item ativo
  useEffect(() => {
    scrollActiveLinkToCenter();
  }, [activeIndex]);

  // Rola o link ativo para o centro da navbar
  const scrollActiveLinkToCenter = () => {
    if (!navRef.current) return;

    const activeLink = navRef.current.querySelector('.category-link.active');
    if (!activeLink) return;

    const container = navRef.current;
    const containerWidth = container.offsetWidth;
    const linkOffsetLeft = activeLink.offsetLeft;
    const linkWidth = activeLink.offsetWidth;

    // Calcula a posição para centralizar o item ativo
    const scrollPosition = linkOffsetLeft - (containerWidth / 2) + (linkWidth / 2);

    container.scrollTo({
      left: scrollPosition,
      behavior: 'smooth',
    });
  };

  // Scroll suave para a categoria
  const handleCategoryClick = (categoryId, categoryName) => {
    setIsScrolling(true);
    setActiveIndex(categories.findIndex((cat) => cat.id === categoryId));

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 800);

    if (onCategoryClick) {
      onCategoryClick(categoryName);
    }
  };

  // Rolagem com wheel na navbar
  const handleWheel = (e) => {
    if (navRef.current) {
      e.preventDefault();
      navRef.current.scrollLeft += e.deltaY;
    }
  };

  // Bottom sheet handlers
  const handleOpenBottomSheet = () => setIsBottomSheetOpen(true);
  const handleCloseBottomSheet = () => setIsBottomSheetOpen(false);

  const handleAllCategoryClick = (categoryId) => {
    const sectionId = sanitizeId(String(categoryId));
    scroller.scrollTo(sectionId, {
      duration: 600,
      delay: 0,
      smooth: 'easeInOutQuart',
      offset: offset,
    });
    setIsBottomSheetOpen(false);
  };

  // Bloquear scroll do body quando bottom sheet aberto
  useEffect(() => {
    document.body.style.overflow = isBottomSheetOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isBottomSheetOpen]);

  // Cor do indicador ativo
  const indicatorColor = 'var(--texto)';

  return (
    <>
    <div className={`categories-navbar ${className}`}>
      {/* Botão mobile - fixo fora do scroll */}
      {shouldShowAllButton && (
        <button
          className="category-all-btn category-all-btn-mobile"
          onClick={handleOpenBottomSheet}
          aria-label={t('todasCategorias')}
        >
          <MenuIcon sx={{ fontSize: 18 }} />
        </button>
      )}

      <div
        className="categories-navbar-wrapper"
        ref={navRef}
        onWheel={handleWheel}
      >
        <ul className="categories-navbar-list">
          {/* Botão desktop - inline na lista */}
          {shouldShowAllButton && (
            <li className="categories-navbar-item categories-navbar-item-all-desktop">
              <button
                className="category-all-btn"
                onClick={handleOpenBottomSheet}
                aria-label={t('todasCategorias')}
              >
                <MenuIcon sx={{ fontSize: 18 }} />
              </button>
            </li>
          )}

          {categories.map((categoria, index) => {
            const sectionId = sanitizeId(String(categoria.id));
            const isActive = activeIndex === index;
            return (
              <li key={categoria.id} className="categories-navbar-item">
                <ScrollLink
                  to={sectionId}
                  spy={true}
                  smooth={true}
                  duration={400}
                  offset={offset}
                  activeClass="active"
                  className={`category-link ${isActive ? 'active' : ''}`}
                  onSetActive={() => {
                    if (!isScrolling) {
                      setActiveIndex(index);
                      scrollActiveLinkToCenter();
                    }
                  }}
                  onClick={() =>
                    handleCategoryClick(categoria.id, categoria.nome)
                  }
                >
                  {categoria.nome}
                  {isActive && (
                    <span
                      className="active-indicator"
                      style={{ backgroundColor: indicatorColor }}
                    />
                  )}
                </ScrollLink>
              </li>
            );
          })}
        </ul>
      </div>
    </div>

    {/* Bottom Sheet - Todas as Categorias */}
    {isBottomSheetOpen && (
      <div className="cat-bottomsheet-overlay" onClick={handleCloseBottomSheet}>
        <div className="cat-bottomsheet-container" onClick={(e) => e.stopPropagation()}>
          <div className="cat-bottomsheet-handle" />
          <div className="cat-bottomsheet-header">
            <h3>{t('todasCategorias')}</h3>
            <IconButton onClick={handleCloseBottomSheet} size="small" aria-label="close">
              <CloseIcon />
            </IconButton>
          </div>
          <div className="cat-bottomsheet-list">
            {categories.map((cat, index) => {
              const isActive = index === activeIndex;
              return (
                <button
                  key={cat.id}
                  className={`cat-bottomsheet-item ${isActive ? 'cat-bottomsheet-item-active' : ''}`}
                  onClick={() => handleAllCategoryClick(cat.id)}
                  style={{ '--theme-color': themeColor || '#333' }}
                >
                  <span className="cat-bottomsheet-item-text">{cat.nome}</span>
                  {isActive ? (
                    <CheckIcon sx={{ fontSize: 20, color: 'var(--theme-color)' }} />
                  ) : (
                    <ChevronRightIcon sx={{ fontSize: 20, color: '#ccc' }} />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default CategoriesNavBar;
